import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Box, Button } from "@noom/wax-component-library";

import type {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { useProgramResult } from "@/hooks";

import { BaseOutline } from "../../core";

import { ProgramResultDescriptionSection } from "./ProgramResultDescriptionSection";
import { ProgramResultFooterSection } from "./ProgramResultFooterSection";
import { ProgramResultHeaderSection } from "./ProgramResultHeaderSection";

const QUESTION_ID = "programResult";

type ProgramResultProps = {
  onClickPrimaryCta: () => void;
  onClickSecondaryCta?: () => void;
};

export const ProgramResult: React.FC<ProgramResultProps> = ({
  onClickPrimaryCta,
  onClickSecondaryCta,
}) => {
  const { t } = useTranslation();
  const { trackProgramResultPageViewedEvent } = useProgramResult();

  useEffect(() => {
    trackProgramResultPageViewedEvent();
  }, []);

  return (
    <BaseOutline hideHeader>
      <Box
        margin="0 auto"
        maxWidth="400px"
        minWidth={{ base: "400px", md: 0 }}
        position="relative"
      >
        <ProgramResultHeaderSection />
        <ProgramResultDescriptionSection />
        <Box
          bottom="0"
          marginTop="var(--spacing-xl)"
          padding="0 var(--spacing-m) var(--spacing-m) var(--spacing-m)"
          position="sticky"
          width="100%"
        >
          <Button colorScheme="primary" onClick={onClickPrimaryCta} size="xl">
            {t("programResult.ctaText.primaryCta")}
          </Button>
        </Box>
        {onClickSecondaryCta && (
          <Box
            padding="0 var(--spacing-m) var(--spacing-m) var(--spacing-m)"
            width="100%"
          >
            <Button onClick={onClickSecondaryCta} size="xl" variant="outline">
              {t("programResult.ctaText.secondaryCta")}
            </Button>
          </Box>
        )}
        <ProgramResultFooterSection />
      </Box>
    </BaseOutline>
  );
};

export const ProgramResultQuestion: React.FC<QuestionComponentProps> = ({
  onClickNext,
}) => <ProgramResult onClickPrimaryCta={() => onClickNext(null)} />;

export const Q_PROGRAM_RESULT: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE"],
  shouldShowQuestion: () => true,
  component: ProgramResultQuestion,
};
